import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PublicClientApplication } from '@azure/msal-browser';
import { useAuthStore } from '../stores/authStore';
import { api } from '../lib/api';
import { msalConfig, loginRequest } from '../lib/msalConfig';

export function LoginPage() {
  const navigate = useNavigate();
  const { setUser, setAzureAccount } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [msalInstance, setMsalInstance] = useState<PublicClientApplication | null>(null);

  useEffect(() => {
    // Initialize MSAL with config from backend
    api.getAzureConfig()
      .then((config: any) => {
        const updatedConfig = {
          ...msalConfig,
          auth: {
            ...msalConfig.auth,
            clientId: config.clientId,
            authority: `https://login.microsoftonline.com/${config.tenantId}`,
          },
        };
        const instance = new PublicClientApplication(updatedConfig);
        instance.initialize().then(() => {
          setMsalInstance(instance);
        });
      })
      .catch((err) => {
        console.error('Failed to load Azure AD config:', err);
        setError('Failed to initialize authentication. Please check backend connection.');
      });
  }, []);

  const handleLogin = async () => {
    if (!msalInstance) {
      setError('Authentication not initialized');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await msalInstance.loginPopup(loginRequest);
      
      if (response && response.account) {
        const account = response.account;
        
        // Set user info
        const user = {
          id: account.localAccountId,
          name: account.name || account.username,
          email: account.username,
          role: 'admin', // Will be determined by backend/groups
        };
        
        setAzureAccount(account);
        setUser(user);
        
        navigate('/dashboard');
      }
    } catch (error: any) {
      console.error('Login failed:', error);
      setError(error.message || 'Failed to sign in. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 to-orange-100">
      <div className="bg-white p-8 rounded-xl shadow-2xl max-w-md w-full">
        <div className="text-center mb-8">
          <div className="text-linepe-orange text-4xl font-bold">LinePe</div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        <button
          onClick={handleLogin}
          disabled={loading || !msalInstance}
          className="w-full bg-[#2F2F2F] hover:bg-[#1F1F1F] disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-lg flex items-center justify-center gap-3 transition-colors"
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              Signing in...
            </>
          ) : (
            <>
              <svg className="w-5 h-5" viewBox="0 0 23 23" fill="currentColor">
                <path d="M0 0h10.952v10.952H0zm12.048 0H23v10.952H12.048zM0 12.048h10.952V23H0zm12.048 0H23V23H12.048z" />
              </svg>
              Sign in with Microsoft
            </>
          )}
        </button>

        <p className="text-sm text-gray-500 text-center mt-6">
          Use your LinePe organizational account to sign in
        </p>
      </div>
    </div>
  );
}

